'use client'

import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react'
import type { UserJWTTokenPayload } from 'shared/lib/auth/payload'
import * as Sentry from '@sentry/nextjs'

type UserContextType = {
  userId: number | null
  email: string | null
  authToken: string
  o2xId: string | null
  profileImage: string | null
  firstName: string | null
  lastName: string | null
  isSuperuser: boolean
  organizationId: number | null
  organizationName: string | null
}

const UserContext = createContext<UserContextType | undefined>(undefined)

interface UserProviderProps {
  children: ReactNode
  payload?: UserJWTTokenPayload | null
}

export const UserProvider = ({
  children,
  payload = null,
}: UserProviderProps) => {
  const [userId, setUserId] = useState<number | null>(payload?.user?.id ?? null)
  const [email, setEmail] = useState<string | null>(
    payload?.user?.email ?? null
  )
  const [authToken, setAuthToken] = useState<string>(payload?.authToken ?? '')
  const [isSuperuser, setIsSuperuser] = useState<boolean>(
    payload?.user?.is_superuser ?? false
  )
  const [o2xId, setO2xId] = useState<string | null>(
    payload?.user?.o2x_id ?? null
  )
  const [profileImage, setProfileImage] = useState<string | null>(
    payload?.user?.profile_image ?? null
  )
  const [firstName, setFirstName] = useState<string | null>(
    payload?.user?.first_name ?? null
  )
  const [lastName, setLastName] = useState<string | null>(
    payload?.user?.last_name ?? null
  )
  const [organizationId, setOrganizationId] = useState<number | null>(
    payload?.user?.organizations_organization?.id ?? null
  )
  const [organizationName, setOrganizationName] = useState<string | null>(
    payload?.user?.organizations_organization?.name ?? null
  )

  useEffect(() => {
    if (payload?.user) {
      Sentry.setUser({
        id: payload.user.id.toString(),
        email: payload.user.email,
      })
      return
    }

    const fetchUserData = async () => {
      try {
        const res = await fetch('/api/auth')
        if (!res.ok) throw new Error('Failed to fetch user data')
        const data = await res.json()

        setUserId(data.userId)
        setEmail(data.email)
        setAuthToken(data.authToken)
        setO2xId(data.o2x_id ?? null)
        setProfileImage(data.profile_image ?? null)
        setFirstName(data.first_name ?? null)
        setLastName(data.last_name ?? null)
        setIsSuperuser(data.is_superuser ?? false)
        setOrganizationId(data.organizations_organization?.id ?? null)
        setOrganizationName(data.organizations_organization?.name ?? null)

        if (data.email) {
          Sentry.setUser({
            id: data.userId.toString(),
            email: data.email,
          })
        } else {
          Sentry.setUser(null)
        }
      } catch (error) {
        console.error('Error fetching user data:', error)
      }
    }

    fetchUserData()
  }, [payload])

  return (
    <UserContext.Provider
      value={{
        userId,
        email,
        authToken,
        o2xId,
        profileImage,
        firstName,
        lastName,
        isSuperuser,
        organizationId,
        organizationName,
      }}
    >
      {children}
    </UserContext.Provider>
  )
}

export const useUserContext = () => {
  const context = useContext(UserContext)
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider.')
  }
  return context
}
